/**
 * Holiday seasons: Halloween and winter decorate the levels, the music and the shell screens
 * for a few weeks a year, by the device's local date. Pure (no DOM): the shell resolves the
 * season once and hands it to the engine (GameSettings.season).
 *
 * The «Праздничное оформление» setting turns a holiday's decorations off; the dev override
 * (`?season=halloween|winter|none` in the page address, also a parameter of the engine
 * harness and the screenshot scripts) replaces the date, the setting still applies.
 */

export const SEASONS = ['halloween', 'winter', 'none'] as const;
export type Season = (typeof SEASONS)[number];
/** A season that is a holiday (not 'none'). */
export type Holiday = Exclude<Season, 'none'>;
export const HOLIDAYS: readonly Holiday[] = ['halloween', 'winter'];

/** A day of the year: month 1..12, day of the month 1..31. */
interface Day {
  month: number;
  day: number;
}

/** The first and the last day of each holiday season, both included. */
export const SEASON_DAYS: Readonly<Record<Holiday, { from: Day; to: Day }>> = {
  halloween: { from: { month: 10, day: 15 }, to: { month: 11, day: 2 } },
  // Across the new year: 15 December to 14 January.
  winter: { from: { month: 12, day: 15 }, to: { month: 1, day: 14 } },
};

/** Days as one comparable number (month × 100 + day). */
const ordinal = ({ month, day }: Day) => month * 100 + day;

function within(date: Day, from: Day, to: Day): boolean {
  const d = ordinal(date);
  const a = ordinal(from);
  const b = ordinal(to);
  // A season across the new year wraps around.
  return a <= b ? d >= a && d <= b : d >= a || d <= b;
}

/** The holiday season of a date, by the device's local calendar. */
export function seasonOn(date: Date): Season {
  const day = { month: date.getMonth() + 1, day: date.getDate() };
  for (const season of HOLIDAYS) {
    const { from, to } = SEASON_DAYS[season];
    if (within(day, from, to)) return season;
  }
  return 'none';
}

export function isSeason(value: unknown): value is Season {
  return typeof value === 'string' && (SEASONS as readonly string[]).includes(value);
}

/** The dev override in a page address' query (`?season=winter`); null if absent or unknown. */
export function seasonOverride(search: string): Season | null {
  let value: string | null = null;
  try {
    value = new URLSearchParams(search).get('season');
  } catch {
    return null;
  }
  return isSeason(value) ? value : null;
}

/** The season of the calendar, or the override when there is one. */
export function calendarSeason(now: Date, override: Season | null = null): Season {
  return override ?? seasonOn(now);
}
